import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Package, ShoppingCart, MapPin, Truck, CheckCircle, Clock, Zap, X as XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STEPS = ["confirmed", "processing", "shipped", "delivered"];
const STATUS_ICONS = { confirmed: CheckCircle, processing: Clock, shipped: Truck, delivered: Package };
const STATUS_LABELS = { confirmed: "Confirmed", processing: "Processing", shipped: "Shipped", delivered: "Delivered" };

const PREORDER_STATUS_COLORS = {
  pending: "text-yellow-400 border-yellow-600/30 bg-yellow-500/5",
  confirmed: "text-[#39FF14] border-[#39FF14]/30 bg-[#39FF14]/5",
  shipped: "text-cyan-400 border-cyan-500/30 bg-cyan-500/5",
  fulfilled: "text-blue-400 border-blue-500/30 bg-blue-500/5",
  cancelled: "text-red-400 border-red-500/30 bg-red-500/5",
};

function StatusTimeline({ currentStatus }) {
  const currentIdx = STATUS_STEPS.indexOf(currentStatus);
  return (
    <div className="flex items-center gap-0 w-full mt-4" data-testid="order-status-timeline">
      {STATUS_STEPS.map((step, i) => {
        const Icon = STATUS_ICONS[step];
        const isActive = i <= currentIdx;
        const isCurrent = i === currentIdx;
        return (
          <div key={step} className="flex items-center flex-1">
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 flex items-center justify-center border ${
                isCurrent ? "border-[#39FF14] bg-[#39FF14]/10" : isActive ? "border-[#39FF14]/50 bg-[#39FF14]/5" : "border-[#1A1A1A] bg-[#0D0D0D]"
              }`}>
                <Icon className={`h-3.5 w-3.5 ${isActive ? "text-[#39FF14]" : "text-[#A1A1A1]/40"}`} strokeWidth={1.5} />
              </div>
              <span className={`font-mono text-[9px] uppercase tracking-wider mt-1.5 ${isActive ? "text-[#39FF14]" : "text-[#A1A1A1]/40"}`}>
                {STATUS_LABELS[step]}
              </span>
            </div>
            {i < STATUS_STEPS.length - 1 && (
              <div className={`flex-1 h-[1px] mx-1 ${i < currentIdx ? "bg-[#39FF14]/50" : "bg-[#1A1A1A]"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function OrdersPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [preorders, setPreorders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("orders");

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login?redirect=/orders");
      return;
    }
    const fetchAll = async () => {
      try {
        const [ordersRes, preordersRes] = await Promise.all([
          axios.get(`${API}/api/orders`),
          axios.get(`${API}/api/preorder/my`),
        ]);
        setOrders(ordersRes.data);
        setPreorders(preordersRes.data);
      } catch (e) {
        console.error("Failed to fetch", e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [user, authLoading, navigate]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050505" }}>
        <div className="w-8 h-8 border border-[#39FF14] border-t-transparent animate-spin" />
      </div>
    );
  }

  const hasNothing = orders.length === 0 && preorders.length === 0;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050505" }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="mb-8">
          <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-2">Account</p>
          <h1 data-testid="orders-heading" className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-[#F2F2F2]" style={{ fontFamily: "Chivo, sans-serif" }}>
            My Orders
          </h1>
        </div>

        {hasNothing ? (
          <div className="border border-[#1A1A1A] bg-[#0D0D0D] p-16 text-center" data-testid="no-orders">
            <Package className="h-12 w-12 text-[#1A1A1A] mx-auto mb-4" strokeWidth={1} />
            <h2 className="text-xl font-bold text-[#F2F2F2] uppercase tracking-tight mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>
              No orders yet
            </h2>
            <p className="text-[#A1A1A1] text-sm mb-6">Once you make a purchase, your orders will appear here.</p>
            <Link to="/products">
              <Button className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold px-8 py-6">
                <ShoppingCart className="h-4 w-4 mr-2" />Start Shopping
              </Button>
            </Link>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex gap-[1px] mb-6">
              <button
                data-testid="tab-orders"
                onClick={() => setTab("orders")}
                className={`flex-1 py-3 font-mono text-xs uppercase tracking-[0.2em] font-bold transition-colors ${tab === "orders" ? "bg-[#39FF14] text-[#050505]" : "bg-[#0D0D0D] text-[#A1A1A1] hover:text-[#39FF14] border border-[#1A1A1A]"}`}
              >
                <Package className="h-3 w-3 inline mr-2" />Orders ({orders.length})
              </button>
              <button
                data-testid="tab-preorders"
                onClick={() => setTab("preorders")}
                className={`flex-1 py-3 font-mono text-xs uppercase tracking-[0.2em] font-bold transition-colors ${tab === "preorders" ? "bg-[#39FF14] text-[#050505]" : "bg-[#0D0D0D] text-[#A1A1A1] hover:text-[#39FF14] border border-[#1A1A1A]"}`}
              >
                <Zap className="h-3 w-3 inline mr-2" />Preorders ({preorders.length})
              </button>
            </div>

            {/* Orders Tab */}
            {tab === "orders" && (
              orders.length === 0 ? (
                <div className="border border-[#1A1A1A] bg-[#0D0D0D] p-12 text-center">
                  <p className="text-[#A1A1A1] text-sm">No regular orders yet.</p>
                </div>
              ) : (
                <div className="space-y-[1px] bg-[#1A1A1A]">
                  {orders.map((order) => (
                    <div key={order.id} data-testid={`order-${order.id}`} className="bg-[#0D0D0D] p-6">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                        <div>
                          <p className="font-mono text-xs text-[#A1A1A1] uppercase tracking-wider mb-1">Order #{order.id.slice(-8)}</p>
                          <p className="font-mono text-xs text-[#A1A1A1]">
                            {new Date(order.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                          </p>
                        </div>
                        <span className="font-mono text-lg font-bold text-[#39FF14]">${order.total.toFixed(2)}</span>
                      </div>
                      <StatusTimeline currentStatus={order.status} />
                      {order.tracking_number && (
                        <div className="mt-4 p-3 border border-[#39FF14]/20 bg-[#39FF14]/5 flex items-center gap-3" data-testid={`tracking-${order.id}`}>
                          <Truck className="h-4 w-4 text-[#39FF14]" strokeWidth={1.5} />
                          <div>
                            <p className="font-mono text-[10px] text-[#39FF14] uppercase tracking-wider">Tracking Number</p>
                            <p className="font-mono text-sm text-[#F2F2F2] font-bold">{order.tracking_number}</p>
                          </div>
                        </div>
                      )}
                      {order.shipping_address && order.shipping_address.full_name && (
                        <div className="mt-4 p-3 border border-[#1A1A1A] flex items-start gap-3">
                          <MapPin className="h-4 w-4 text-[#A1A1A1] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                          <div className="text-xs text-[#A1A1A1]">
                            <p className="text-[#F2F2F2]">{order.shipping_address.full_name}</p>
                            <p>{order.shipping_address.address_line1}</p>
                            {order.shipping_address.address_line2 && <p>{order.shipping_address.address_line2}</p>}
                            <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}</p>
                          </div>
                        </div>
                      )}
                      <div className="space-y-2 mt-4 pt-4 border-t border-[#1A1A1A]">
                        {order.items.map((item, idx) => (
                          <div key={`${item.product_id}-${idx}`} className="flex items-center gap-3 text-sm">
                            <div className="w-10 h-10 border border-[#1A1A1A] flex-shrink-0 overflow-hidden">
                              {item.image_url ? (
                                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full bg-[#0D0D0D] flex items-center justify-center">
                                  <span className="font-mono text-[#1A1A1A] text-xs">X</span>
                                </div>
                              )}
                            </div>
                            <span className="text-[#F2F2F2]">{item.name}{item.selected_color ? ` (${item.selected_color})` : ""}</span>
                            <span className="text-[#A1A1A1] font-mono text-xs">x{item.quantity}</span>
                            <span className="font-mono text-[#A1A1A1] text-xs ml-auto">${(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}

            {/* Preorders Tab */}
            {tab === "preorders" && (
              preorders.length === 0 ? (
                <div className="border border-[#1A1A1A] bg-[#0D0D0D] p-12 text-center">
                  <p className="text-[#A1A1A1] text-sm">No preorders yet.</p>
                </div>
              ) : (
                <div className="space-y-[1px] bg-[#1A1A1A]">
                  {preorders.map((po) => {
                    const statusStyle = PREORDER_STATUS_COLORS[po.status] || "text-[#A1A1A1] border-[#1A1A1A]";
                    return (
                      <div key={po.id} data-testid={`preorder-${po.id}`} className="bg-[#0D0D0D] p-6">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Zap className="h-3.5 w-3.5 text-[#39FF14]" />
                              <p className="font-mono text-xs text-[#A1A1A1] uppercase tracking-wider">Preorder #{po.id.slice(-8)}</p>
                            </div>
                            <p className="font-mono text-xs text-[#A1A1A1]">
                              {new Date(po.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="font-mono text-lg font-bold text-[#39FF14]">${po.amount?.toFixed(2)}</span>
                            <div className={`inline-block ml-3 px-2 py-0.5 border text-[10px] font-mono uppercase tracking-wider ${statusStyle}`}>
                              {po.status}
                            </div>
                          </div>
                        </div>

                        {/* Product info */}
                        <div className="flex items-center gap-3 p-3 border border-[#1A1A1A]">
                          <Zap className="h-4 w-4 text-[#39FF14] flex-shrink-0" />
                          <div className="flex-1">
                            <Link to={`/products/${po.product_id}`} className="text-[#F2F2F2] text-sm font-bold hover:text-[#39FF14] transition-colors">
                              {po.product_name}
                            </Link>
                            <div className="flex gap-4 mt-1">
                              <span className="font-mono text-xs text-[#A1A1A1]">Qty: {po.quantity || 1}</span>
                              <span className="font-mono text-xs text-[#A1A1A1]">Price: ${po.price?.toFixed(2)} ea.</span>
                              <span className="font-mono text-xs text-[#A1A1A1]">Shipping: ${po.shipping_cost?.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Shipping address */}
                        {po.shipping_address && po.shipping_address.full_name && (
                          <div className="mt-3 p-3 border border-[#1A1A1A] flex items-start gap-3">
                            <MapPin className="h-4 w-4 text-[#A1A1A1] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                            <div className="text-xs text-[#A1A1A1]">
                              <p className="text-[#F2F2F2]">{po.shipping_address.full_name}</p>
                              <p>{po.shipping_address.address_line1}</p>
                              {po.shipping_address.address_line2 && <p>{po.shipping_address.address_line2}</p>}
                              <p>{po.shipping_address.city}, {po.shipping_address.state} {po.shipping_address.zip_code}</p>
                            </div>
                          </div>
                        )}

                        {/* Tracking number */}
                        {po.tracking_number && (
                          <div className="mt-3 p-3 border border-[#39FF14]/20 bg-[#39FF14]/5 flex items-center gap-3" data-testid={`preorder-tracking-${po.id}`}>
                            <Truck className="h-4 w-4 text-[#39FF14]" strokeWidth={1.5} />
                            <div>
                              <p className="font-mono text-[10px] text-[#39FF14] uppercase tracking-wider">Tracking Number</p>
                              <p className="font-mono text-sm text-[#F2F2F2] font-bold">{po.tracking_number}</p>
                            </div>
                          </div>
                        )}

                        {/* Status message */}
                        {po.status === "confirmed" && (
                          <p className="text-[#A1A1A1] text-xs mt-3 font-mono">Your product is guaranteed. We'll notify you when it ships.</p>
                        )}
                        {po.status === "shipped" && (
                          <p className="text-cyan-400 text-xs mt-3 font-mono">Your preorder has been shipped! Check above for tracking info.</p>
                        )}
                        {po.status === "fulfilled" && (
                          <p className="text-blue-400 text-xs mt-3 font-mono">This preorder has been fulfilled and shipped!</p>
                        )}
                        {po.status === "cancelled" && (
                          <p className="text-red-400 text-xs mt-3 font-mono">This preorder was cancelled.</p>
                        )}
                        {po.status === "pending" && (
                          <p className="text-yellow-400 text-xs mt-3 font-mono">Payment pending — please complete checkout.</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
}
